import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiRotateCcw, FiImage, FiPlus, FiTrash2 } = FiIcons;

const ThemeSettings = () => {
  const { themeConfig, updateThemeConfig, resetToDefault } = useTheme();
  const [activeTab, setActiveTab] = useState('hero');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [localConfig, setLocalConfig] = useState(themeConfig);

  const tabs = [
    { id: 'hero', label: 'Hero Section' },
    { id: 'about', label: 'About' },
    { id: 'menu', label: 'Menu' },
    { id: 'openKitchen', label: 'Open Kitchen' },
    { id: 'catering', label: 'Catering' },
    { id: 'photoCorner', label: 'Photo Corner' },
    { id: 'contact', label: 'Contact' },
    { id: 'footer', label: 'Footer' }
  ];

  const handleChange = (section, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    setUnsavedChanges(true);
  };

  const handleArrayChange = (section, field, index, value) => {
    setLocalConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].map((item, i) => i === index ? value : item)
      }
    }));
    setUnsavedChanges(true);
  };

  const addArrayItem = (section, field, defaultValue) => {
    setLocalConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: [...prev[section][field], defaultValue]
      }
    }));
    setUnsavedChanges(true);
  };

  const removeArrayItem = (section, field, index) => {
    setLocalConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].filter((_, i) => i !== index)
      }
    }));
    setUnsavedChanges(true);
  };

  const handleSave = () => {
    updateThemeConfig(localConfig);
    setUnsavedChanges(false);
  };

  const handleReset = () => {
    resetToDefault();
    setLocalConfig(themeConfig);
    setUnsavedChanges(false);
  };

  const renderHeroSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-warm-brown font-medium mb-2">Title</label>
        <input
          type="text"
          value={localConfig.hero.title}
          onChange={(e) => handleChange('hero', 'title', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>
      
      <div>
        <label className="block text-warm-brown font-medium mb-2">Subtitle</label>
        <input
          type="text"
          value={localConfig.hero.subtitle}
          onChange={(e) => handleChange('hero', 'subtitle', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Description</label>
        <textarea
          value={localConfig.hero.description}
          onChange={(e) => handleChange('hero', 'description', e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Background Image URL</label>
        <input
          type="url"
          value={localConfig.hero.backgroundImage}
          onChange={(e) => handleChange('hero', 'backgroundImage', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-warm-brown font-medium mb-2">Primary Button Text</label>
          <input
            type="text"
            value={localConfig.hero.primaryButtonText}
            onChange={(e) => handleChange('hero', 'primaryButtonText', e.target.value)}
            className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
        </div>
        <div>
          <label className="block text-warm-brown font-medium mb-2">Primary Button Link</label>
          <input
            type="text"
            value={localConfig.hero.primaryButtonLink}
            onChange={(e) => handleChange('hero', 'primaryButtonLink', e.target.value)}
            className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-warm-brown font-medium mb-2">Secondary Button Text</label>
          <input
            type="text"
            value={localConfig.hero.secondaryButtonText}
            onChange={(e) => handleChange('hero', 'secondaryButtonText', e.target.value)}
            className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
        </div>
        <div>
          <label className="block text-warm-brown font-medium mb-2">Secondary Button Link</label>
          <input
            type="text"
            value={localConfig.hero.secondaryButtonLink}
            onChange={(e) => handleChange('hero', 'secondaryButtonLink', e.target.value)}
            className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
        </div>
      </div>
    </div>
  );

  const renderAboutSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-warm-brown font-medium mb-2">Title</label>
        <input
          type="text"
          value={localConfig.about.title}
          onChange={(e) => handleChange('about', 'title', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Image URL</label>
        <input
          type="url"
          value={localConfig.about.image}
          onChange={(e) => handleChange('about', 'image', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-warm-brown font-medium">Paragraphs</label>
          <button
            onClick={() => addArrayItem('about', 'paragraphs', '')}
            className="flex items-center gap-2 text-warm-brown hover:text-warm-brown/80 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="text-sm" />
            Add Paragraph
          </button>
        </div>
        {localConfig.about.paragraphs.map((paragraph, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <textarea
              value={paragraph}
              onChange={(e) => handleArrayChange('about', 'paragraphs', index, e.target.value)}
              rows={3}
              className="flex-1 px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
              placeholder={`Paragraph ${index + 1}`}
            />
            <button
              onClick={() => removeArrayItem('about', 'paragraphs', index)}
              className="text-red-500 hover:text-red-700 transition-colors p-2"
            >
              <SafeIcon icon={FiTrash2} className="text-sm" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMenuSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-warm-brown font-medium mb-2">Title</label>
        <input
          type="text"
          value={localConfig.menu.title}
          onChange={(e) => handleChange('menu', 'title', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Subtitle</label>
        <textarea
          value={localConfig.menu.subtitle}
          onChange={(e) => handleChange('menu', 'subtitle', e.target.value)}
          rows={2}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-warm-brown font-medium mb-2">Button Text</label>
          <input
            type="text"
            value={localConfig.menu.buttonText}
            onChange={(e) => handleChange('menu', 'buttonText', e.target.value)}
            className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
        </div>
        <div>
          <label className="block text-warm-brown font-medium mb-2">Button Link</label>
          <input
            type="text"
            value={localConfig.menu.buttonLink}
            onChange={(e) => handleChange('menu', 'buttonLink', e.target.value)}
            className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
        </div>
      </div>

      <div>
        <h4 className="text-lg font-playfair text-warm-brown mb-4">Menu Categories</h4>
        {localConfig.menu.categories.map((category, index) => (
          <div key={index} className="bg-cream p-4 rounded-lg mb-4">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-warm-brown font-medium mb-2">Category Title</label>
                <input
                  type="text"
                  value={category.title}
                  onChange={(e) => {
                    const newCategories = [...localConfig.menu.categories];
                    newCategories[index].title = e.target.value;
                    handleChange('menu', 'categories', newCategories);
                  }}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>
              <div>
                <label className="block text-warm-brown font-medium mb-2">Image URL</label>
                <input
                  type="url"
                  value={category.image}
                  onChange={(e) => {
                    const newCategories = [...localConfig.menu.categories];
                    newCategories[index].image = e.target.value;
                    handleChange('menu', 'categories', newCategories);
                  }}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-warm-brown font-medium mb-2">Subtitle</label>
              <input
                type="text"
                value={category.subtitle}
                onChange={(e) => {
                  const newCategories = [...localConfig.menu.categories];
                  newCategories[index].subtitle = e.target.value;
                  handleChange('menu', 'categories', newCategories);
                }}
                className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
              />
            </div>
            <div>
              <label className="block text-warm-brown font-medium mb-2">Items (comma separated)</label>
              <input
                type="text"
                value={category.items.join(', ')}
                onChange={(e) => {
                  const newCategories = [...localConfig.menu.categories];
                  newCategories[index].items = e.target.value.split(', ').filter(item => item.trim());
                  handleChange('menu', 'categories', newCategories);
                }}
                className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContactSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-warm-brown font-medium mb-2">Title</label>
        <input
          type="text"
          value={localConfig.contact.title}
          onChange={(e) => handleChange('contact', 'title', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Subtitle</label>
        <input
          type="text"
          value={localConfig.contact.subtitle}
          onChange={(e) => handleChange('contact', 'subtitle', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Location</label>
        <input
          type="text"
          value={localConfig.contact.location}
          onChange={(e) => handleChange('contact', 'location', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Image URL</label>
        <input
          type="url"
          value={localConfig.contact.image}
          onChange={(e) => handleChange('contact', 'image', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-warm-brown font-medium mb-2">Directions Button Text</label>
          <input
            type="text"
            value={localConfig.contact.directionsButtonText}
            onChange={(e) => handleChange('contact', 'directionsButtonText', e.target.value)}
            className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
        </div>
        <div>
          <label className="block text-warm-brown font-medium mb-2">Directions Button Link</label>
          <input
            type="text"
            value={localConfig.contact.directionsButtonLink}
            onChange={(e) => handleChange('contact', 'directionsButtonLink', e.target.value)}
            className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-warm-brown font-medium mb-2">Order Button Text</label>
          <input
            type="text"
            value={localConfig.contact.orderButtonText}
            onChange={(e) => handleChange('contact', 'orderButtonText', e.target.value)}
            className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
        </div>
        <div>
          <label className="block text-warm-brown font-medium mb-2">Order Button Link</label>
          <input
            type="text"
            value={localConfig.contact.orderButtonLink}
            onChange={(e) => handleChange('contact', 'orderButtonLink', e.target.value)}
            className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
        </div>
      </div>

      <div>
        <h4 className="text-lg font-playfair text-warm-brown mb-4">Business Hours</h4>
        {localConfig.contact.hours.map((schedule, index) => (
          <div key={index} className="grid md:grid-cols-2 gap-4 mb-2">
            <div>
              <label className="block text-warm-brown font-medium mb-1">Days</label>
              <input
                type="text"
                value={schedule.days}
                onChange={(e) => {
                  const newHours = [...localConfig.contact.hours];
                  newHours[index].days = e.target.value;
                  handleChange('contact', 'hours', newHours);
                }}
                className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
              />
            </div>
            <div>
              <label className="block text-warm-brown font-medium mb-1">Time</label>
              <input
                type="text"
                value={schedule.time}
                onChange={(e) => {
                  const newHours = [...localConfig.contact.hours];
                  newHours[index].time = e.target.value;
                  handleChange('contact', 'hours', newHours);
                }}
                className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderFooterSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-warm-brown font-medium mb-2">Logo URL</label>
        <input
          type="url"
          value={localConfig.footer.logo}
          onChange={(e) => handleChange('footer', 'logo', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Title</label>
        <input
          type="text"
          value={localConfig.footer.title}
          onChange={(e) => handleChange('footer', 'title', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Slogan</label>
        <input
          type="text"
          value={localConfig.footer.slogan}
          onChange={(e) => handleChange('footer', 'slogan', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Facebook Link</label>
        <input
          type="text"
          value={localConfig.footer.facebookLink}
          onChange={(e) => handleChange('footer', 'facebookLink', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Instagram Link</label>
        <input
          type="text"
          value={localConfig.footer.instagramLink}
          onChange={(e) => handleChange('footer', 'instagramLink', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Maps Link</label>
        <input
          type="text"
          value={localConfig.footer.mapsLink}
          onChange={(e) => handleChange('footer', 'mapsLink', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>

      <div>
        <label className="block text-warm-brown font-medium mb-2">Copyright</label>
        <input
          type="text"
          value={localConfig.footer.copyright}
          onChange={(e) => handleChange('footer', 'copyright', e.target.value)}
          className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
        />
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'hero':
        return renderHeroSettings();
      case 'about':
        return renderAboutSettings();
      case 'menu':
        return renderMenuSettings();
      case 'contact':
        return renderContactSettings();
      case 'footer':
        return renderFooterSettings();
      default:
        return <div>Content for {activeTab} section coming soon...</div>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-playfair text-warm-brown mb-2">Theme Settings</h2>
          <p className="text-warm-brown/70">Customize your website content and appearance</p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 border border-warm-brown/20 text-warm-brown rounded-lg hover:bg-warm-brown/5 transition-colors"
          >
            <SafeIcon icon={FiRotateCcw} className="text-sm" />
            Reset to Default
          </button>
          <button
            onClick={handleSave}
            disabled={!unsavedChanges}
            className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={FiSave} className="text-sm" />
            Save Changes
          </button>
        </div>
      </div>

      {unsavedChanges && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-orange-50 border border-orange-200 text-orange-700 px-4 py-3 rounded-lg"
        >
          You have unsaved changes. Don't forget to save your work!
        </motion.div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="border-b border-warm-brown/10">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-warm-brown border-b-2 border-warm-brown bg-cream/50'
                    : 'text-warm-brown/70 hover:text-warm-brown hover:bg-cream/30'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ThemeSettings;